import { Chromosome, Particle } from '../types/algorithm';
import { OptimizationParameters, OptimizationResults, OptimizationResultsWithStats, AlgorithmResult, ChartDataPoint, FitnessMode, EnergyAwareWeights } from '../types';
import { parseQWSDataset, buildQualityMatrix } from '../utils/qwsParser';
import { calculateResponseTime, calculateEnergy, calculateCost, FitnessCalculator, CostCalculator, setGlobalFitnessCalculator, clearGlobalFitnessCalculator, setGlobalCostCalculator, clearGlobalCostCalculator, DEFAULT_ENERGY_AWARE_WEIGHTS } from '../utils/fitnessCalculator';
import { generateRunSeed } from '../utils/seededRNG';
import { runGeneticAlgorithm } from '../algorithms/geneticAlgorithm';
import { runCulturalAlgorithm } from '../algorithms/culturalAlgorithm';
import { runSFLA } from '../algorithms/sfla';
import { runSFLAGABaseline } from '../algorithms/sflaGaBaseline';
import { runSFLAGA } from '../algorithms/sflaga';
import { runPSO } from '../algorithms/pso';
import { runGAPSO } from '../algorithms/gapso';
import { calculateAlgorithmStatistics } from '../utils/statistics';
import { normalizeAcrossAlgorithms } from '../utils/normalization';
import { calculateCompositeScoreStatistics, DEFAULT_COMPOSITE_WEIGHTS } from '../utils/compositeScore';
import { performParetoAnalysis } from '../utils/paretoAnalysis';
import { SHARED_PARAMETER_POLICY, resolveSflagaEnhancements } from '../utils/parameterUtils';
import { normalizeEnergyAwareWeights } from '../utils/energyAwareWeights';
import { ParallelOptimizationRunner } from './parallelOptimizationRunner';

export interface ConvergenceData {
  ga: ChartDataPoint[];
  culture: ChartDataPoint[];
  frog: ChartDataPoint[];
  sflaGa?: ChartDataPoint[];
  sflaga: ChartDataPoint[];
  pso: ChartDataPoint[];
  gapso: ChartDataPoint[];
}

export class OptimizationService {
  private matrix: number[][][] | null = null;
  private services: any[] = [];
  private taskCount: number = 0;
  private serviceCount: number = 0;
  private fitnessCalculator: FitnessCalculator | null = null;
  private costCalculator: CostCalculator | null = null;
  private fitnessMode: FitnessMode = 'energy-aware';
  private energyAwareWeights: EnergyAwareWeights = DEFAULT_ENERGY_AWARE_WEIGHTS;
  private matrixSeed: number | null = null; // Seed used for QWS matrix generation (for reproducibility)
  private parallelRunner = new ParallelOptimizationRunner();

  /**
   * Loads and parses QWS dataset
   * @param csvText CSV text content
   * @param taskCount Number of tasks
   * @param fitnessMode Fitness calculation mode (default: 'energy-aware')
   * @param energyAwareWeights Weights for energy-aware mode (only used if fitnessMode is 'energy-aware')
   * @param seed Optional seed for QWS matrix generation. If not provided, generates a deterministic seed.
   *             For thesis experiments, provide a fixed seed and log it for reproducibility.
   */
  loadDataset(
    csvText: string, 
    taskCount: number,
    fitnessMode: FitnessMode = 'energy-aware',
    energyAwareWeights: EnergyAwareWeights = DEFAULT_ENERGY_AWARE_WEIGHTS,
    seed?: number,
    taskSpecificVariationEnabled: boolean = true
  ): void {
    // Clean up previous calculators to avoid stale state
    this.dispose();
    
    this.services = parseQWSDataset(csvText);
    this.serviceCount = this.services.length;
    this.taskCount = taskCount;
    
    // Build quality matrix with seeded RNG for reproducibility
    const { matrix, seed: actualSeed } = buildQualityMatrix(
      this.services,
      taskCount,
      seed,
      taskSpecificVariationEnabled
    );
    this.matrix = matrix;
    this.matrixSeed = actualSeed;
    
    // Log seed for reproducibility (critical for thesis experiments)
    console.log(
      `[OptimizationService] QWS Matrix built with seed: ${actualSeed} (taskCount: ${taskCount}, services: ${this.serviceCount}, variation: ${taskSpecificVariationEnabled ? 'on' : 'off'})`
    );
    
    const resolvedEnergyAwareWeights = normalizeEnergyAwareWeights(energyAwareWeights);

    this.fitnessMode = fitnessMode;
    this.energyAwareWeights = resolvedEnergyAwareWeights;
    
    // Initialize cost calculator with cached service prices (PERFORMANCE OPTIMIZATION)
    // This must be initialized before fitness calculator if using energy-aware mode
    if (this.matrix) {
      this.costCalculator = new CostCalculator();
      this.costCalculator.initialize(this.matrix, taskCount, this.serviceCount);
      setGlobalCostCalculator(this.costCalculator);
      
      // Initialize fitness calculator with cached min/max values (PERFORMANCE OPTIMIZATION)
      // For energy-aware mode, also precomputes the energy range
      // Use matrix seed for fitness calculator sampling (reproducibility)
      this.fitnessCalculator = new FitnessCalculator();
      this.fitnessCalculator.initialize(
        this.matrix, 
        taskCount, 
        this.serviceCount,
        fitnessMode,
        resolvedEnergyAwareWeights,
        this.costCalculator,
        actualSeed // Pass seed for reproducible sampling
      );
      setGlobalFitnessCalculator(this.fitnessCalculator);
    }
  }
  
  /**
   * Gets the seed used for the current QWS matrix
   * @returns The seed used for matrix generation, or null if no matrix has been built yet
   */
  getMatrixSeed(): number | null {
    return this.matrixSeed;
  }

  /**
   * Disposes calculators and clears global state
   * Call this when dataset/taskCount changes to avoid stale state
   */
  dispose(): void {
    this.parallelRunner.cancel();

    // Clear caches
    if (this.fitnessCalculator) {
      this.fitnessCalculator.clearCache();
    }
    
    // Clear global calculators
    clearGlobalFitnessCalculator();
    clearGlobalCostCalculator();
    
    // Reset references
    this.fitnessCalculator = null;
    this.costCalculator = null;
  }

  cancelActiveWork(): void {
    this.parallelRunner.cancel();
  }

  /**
   * Runs all optimization algorithms
   */
  async runOptimization(
    parameters: OptimizationParameters,
    onAlgorithmStart?: (algorithm: string) => void,
    onGenerationUpdate?: (algorithm: string, generation: number, fitness: number) => void,
    onAlgorithmComplete?: (algorithm: string, result: AlgorithmResult, partialResults: Partial<OptimizationResults>) => void,
    shouldCancel?: () => boolean,
    runNumber: number = 1
  ): Promise<{
    results: OptimizationResults;
    convergenceData: ConvergenceData;
  }> {
    if (!this.matrix) {
      throw new Error('Dataset not loaded');
    }

    if (parameters.parallelExecution && typeof Worker !== 'undefined') {
      const includeSflaGaBaseline = parameters.includeSflaGaBaseline ?? false;
      const sflagaEnhancements = resolveSflagaEnhancements(parameters.sflagaEnhancements);
      const baseSeed = this.matrixSeed ?? Math.floor(Date.now() % 2147483647);
      const runSeed = generateRunSeed(baseSeed, runNumber);
      const fitnessMode = parameters.fitnessMode ?? this.fitnessMode;
      const rawEnergyAwareWeights = parameters.energyAwareWeights ?? this.energyAwareWeights;
      const energyAwareWeights = normalizeEnergyAwareWeights(rawEnergyAwareWeights);
      const normalizedParameters: OptimizationParameters = {
        ...parameters,
        sflagaEnhancements,
      };

      return this.parallelRunner.run({
        matrix: this.matrix,
        taskCount: this.taskCount,
        serviceCount: this.serviceCount,
        parameters: normalizedParameters,
        includeSflaGaBaseline,
        fitnessMode,
        energyAwareWeights,
        matrixSeed: this.matrixSeed,
        runSeed,
        onAlgorithmStart,
        onGenerationUpdate,
        onAlgorithmComplete,
        shouldCancel,
      });
    }

    const includeSflaGaBaseline = parameters.includeSflaGaBaseline ?? false;
    const sflagaEnhancements = resolveSflagaEnhancements(parameters.sflagaEnhancements);
    
    // Generate seed for this run (reproducible per run)
    // Use matrix seed as base, then derive run-specific seed
    const baseSeed = this.matrixSeed ?? Math.floor(Date.now() % 2147483647);
    const runSeed = generateRunSeed(baseSeed, runNumber);

    // Update fitness mode and weights if provided in parameters
    const fitnessMode = parameters.fitnessMode ?? this.fitnessMode;
    const rawEnergyAwareWeights = parameters.energyAwareWeights ?? this.energyAwareWeights;
    const energyAwareWeights = normalizeEnergyAwareWeights(rawEnergyAwareWeights);

    // Reinitialize calculators if mode or weights changed
    if (fitnessMode !== this.fitnessMode || 
        (fitnessMode === 'energy-aware' && 
         (energyAwareWeights.fitness !== this.energyAwareWeights.fitness ||
          energyAwareWeights.energy !== this.energyAwareWeights.energy))) {
      if (this.fitnessCalculator && this.matrix) {
        this.fitnessMode = fitnessMode;
        this.energyAwareWeights = energyAwareWeights;
        this.fitnessCalculator.initialize(
          this.matrix,
          this.taskCount,
          this.serviceCount,
          fitnessMode,
          energyAwareWeights,
          this.costCalculator
        );
      }
    }

    const convergenceData: ConvergenceData = {
      ga: [],
      culture: [],
      frog: [],
      sflaGa: [],
      sflaga: [],
      pso: [],
      gapso: [],
    };

    // Check for cancellation before starting GA
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run GA
    if (onAlgorithmStart) onAlgorithmStart('GA');
    const gaStartTime = performance.now();
    const gaResult = await runGeneticAlgorithm(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness) => {
        convergenceData.ga.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('GA', gen, fitness);
      },
      runSeed // Pass seed for reproducibility
    );
    const gaExecutionTime = performance.now() - gaStartTime;
    
    // Convert and notify GA completion
    const convertChromosomeToResult = (chrom: Chromosome, execTime: number): AlgorithmResult => ({
      fitness: chrom.fitness,
      solution: chrom.x,
      energy: calculateEnergy(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      cost: calculateCost(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      responseTime: calculateResponseTime(chrom.x, this.matrix!, this.taskCount, this.serviceCount),
      executionTime: execTime,
    });
    
    const gaResultConverted = convertChromosomeToResult(gaResult, gaExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('GA', gaResultConverted, { ga: gaResultConverted });
    }

    // Check for cancellation before starting Cultural Algorithm
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run Cultural Algorithm
    if (onAlgorithmStart) onAlgorithmStart('Cultural');
    const cultureStartTime = performance.now();
    const cultureResult = await runCulturalAlgorithm(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
      },
      (gen, fitness) => {
        convergenceData.culture.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('Cultural', gen, fitness);
      },
      runSeed // Pass seed for reproducibility
    );
    const cultureExecutionTime = performance.now() - cultureStartTime;
    
    const cultureResultConverted = convertChromosomeToResult(cultureResult, cultureExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('Cultural', cultureResultConverted, { ga: gaResultConverted, culture: cultureResultConverted });
    }

    // Check for cancellation before starting SFLA
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run SFLA
    if (onAlgorithmStart) onAlgorithmStart('SFLA');
    const frogStartTime = performance.now();
    const frogResult = await runSFLA(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        memeplexCount: parameters.memeplexCount,
      },
      (gen, fitness) => {
        convergenceData.frog.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('SFLA', gen, fitness);
      },
      runSeed // Pass seed for reproducibility
    );
    const frogExecutionTime = performance.now() - frogStartTime;
    
    const frogResultConverted = convertChromosomeToResult(frogResult, frogExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('SFLA', frogResultConverted, { ga: gaResultConverted, culture: cultureResultConverted, frog: frogResultConverted });
    }

    let sflaGaResultConverted: AlgorithmResult | undefined;

    if (includeSflaGaBaseline) {
      // Check for cancellation before starting the standard SFLA-GA baseline
      if (shouldCancel && shouldCancel()) {
        throw new Error('Optimization cancelled');
      }

      if (onAlgorithmStart) onAlgorithmStart('SFLA-GA');
      const sflaGaStartTime = performance.now();
      const sflaGaResult = await runSFLAGABaseline(
        this.taskCount,
        this.serviceCount,
        this.matrix,
        {
          populationSize: parameters.initialPopulation,
          generations: parameters.iterations,
          memeplexCount: parameters.memeplexCount,
          selectionRate: parameters.selectionRate,
          crossoverRate: parameters.crossoverRate,
          mutationRate: parameters.mutationRate,
        },
        (gen, fitness) => {
          convergenceData.sflaGa = convergenceData.sflaGa ?? [];
          convergenceData.sflaGa.push({ iteration: gen, fitness });
          if (onGenerationUpdate) onGenerationUpdate('SFLA-GA', gen, fitness);
        },
        runSeed // Pass seed for reproducibility
      );
      const sflaGaExecutionTime = performance.now() - sflaGaStartTime;

      sflaGaResultConverted = convertChromosomeToResult(sflaGaResult, sflaGaExecutionTime);
      if (onAlgorithmComplete) {
        onAlgorithmComplete('SFLA-GA', sflaGaResultConverted, {
          ga: gaResultConverted,
          culture: cultureResultConverted,
          frog: frogResultConverted,
          sflaGa: sflaGaResultConverted,
        });
      }
    }

    // Check for cancellation before starting the proposed SFLAGA
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }

    if (onAlgorithmStart) onAlgorithmStart('SFLAGA');
    const sflagaStartTime = performance.now();
    const sflagaResult = await runSFLAGA(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        memeplexCount: parameters.memeplexCount,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
        mutationRate: parameters.mutationRate,
        heavyMode: parameters.heavyMode,
        sflagaEnhancements,
      },
      (gen, fitness) => {
        convergenceData.sflaga.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('SFLAGA', gen, fitness);
      },
      runSeed // Pass seed for reproducibility
    );
    const sflagaExecutionTime = performance.now() - sflagaStartTime;

    const sflagaResultConverted = convertChromosomeToResult(sflagaResult, sflagaExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('SFLAGA', sflagaResultConverted, {
        ga: gaResultConverted,
        culture: cultureResultConverted,
        frog: frogResultConverted,
        ...(sflaGaResultConverted ? { sflaGa: sflaGaResultConverted } : {}),
        sflaga: sflagaResultConverted,
      });
    }

    // Check for cancellation before starting PSO
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run PSO
    if (onAlgorithmStart) onAlgorithmStart('PSO');
    const psoStartTime = performance.now();
    const psoResult = await runPSO(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
      },
      (gen, fitness) => {
        convergenceData.pso.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('PSO', gen, fitness);
      },
      runSeed // Pass seed for reproducibility
    );
    const psoExecutionTime = performance.now() - psoStartTime;
    
    const convertParticleToResult = (particle: Particle, execTime: number): AlgorithmResult => ({
      fitness: particle.fitness,
      solution: particle.position,
      energy: calculateEnergy(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      cost: calculateCost(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      responseTime: calculateResponseTime(particle.position, this.matrix!, this.taskCount, this.serviceCount),
      executionTime: execTime,
    });
    
    const psoResultConverted = convertParticleToResult(psoResult, psoExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('PSO', psoResultConverted, { 
        ga: gaResultConverted, 
        culture: cultureResultConverted, 
        frog: frogResultConverted,
        ...(sflaGaResultConverted ? { sflaGa: sflaGaResultConverted } : {}),
        sflaga: sflagaResultConverted,
        pso: psoResultConverted 
      });
    }

    // Check for cancellation before starting GAPSO
    if (shouldCancel && shouldCancel()) {
      throw new Error('Optimization cancelled');
    }
    
    // Run GAPSO
    if (onAlgorithmStart) onAlgorithmStart('GAPSO');
    const gapsoStartTime = performance.now();
    const gapsoResult = await runGAPSO(
      this.taskCount,
      this.serviceCount,
      this.matrix,
      {
        populationSize: parameters.initialPopulation,
        generations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossoverRate: parameters.crossoverRate,
      },
      (gen, fitness) => {
        convergenceData.gapso.push({ iteration: gen, fitness });
        if (onGenerationUpdate) onGenerationUpdate('GAPSO', gen, fitness);
      },
      runSeed // Pass seed for reproducibility
    );

    const gapsoExecutionTime = performance.now() - gapsoStartTime;
    
    const gapsoResultConverted = convertParticleToResult(gapsoResult, gapsoExecutionTime);
    if (onAlgorithmComplete) {
      onAlgorithmComplete('GAPSO', gapsoResultConverted, { 
        ga: gaResultConverted, 
        culture: cultureResultConverted, 
        frog: frogResultConverted,
        ...(sflaGaResultConverted ? { sflaGa: sflaGaResultConverted } : {}),
        sflaga: sflagaResultConverted,
        pso: psoResultConverted,
        gapso: gapsoResultConverted 
      });
    }

    const results: OptimizationResults = {
      ga: convertChromosomeToResult(gaResult, gaExecutionTime),
      culture: convertChromosomeToResult(cultureResult, cultureExecutionTime),
      frog: convertChromosomeToResult(frogResult, frogExecutionTime),
      ...(sflaGaResultConverted ? { sflaGa: sflaGaResultConverted } : {}),
      sflaga: convertChromosomeToResult(sflagaResult, sflagaExecutionTime),
      pso: convertParticleToResult(psoResult, psoExecutionTime),
      gapso: convertParticleToResult(gapsoResult, gapsoExecutionTime),
      configuration: {
        taskCount: parameters.taskCount,
        propertyCount: parameters.qosProperties,
        initialPopulation: parameters.initialPopulation,
        iterations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossover: parameters.crossoverRate,
        mutation: parameters.mutationRate,
        memeplexCount: parameters.memeplexCount,
        includeSflaGaBaseline,
        parallelExecution: parameters.parallelExecution ?? false,
        heavyMode: parameters.heavyMode ?? false,
        fitnessMode: parameters.fitnessMode,
        energyAwareWeights: fitnessMode === 'energy-aware' ? energyAwareWeights : undefined,
        sflagaEnhancements,
        parameterPolicy: parameters.parameterPolicy ?? SHARED_PARAMETER_POLICY,
        seed: this.matrixSeed ?? undefined, // Include matrix seed for reproducibility
        runSeed: runSeed, // Include run seed for reproducibility
        taskSpecificVariationEnabled: parameters.taskSpecificVariationEnabled ?? true,
      },
    };

    return { results, convergenceData };
  }

  /**
   * Runs optimization multiple times and calculates statistics
   * @param skipHeavyCalculations - If true, skips normalization, composite scores, and Pareto analysis (for complete run optimization)
   */
  async runOptimizationWithStatistics(
    parameters: OptimizationParameters,
    onAlgorithmStart?: (algorithm: string) => void,
    onGenerationUpdate?: (algorithm: string, generation: number, fitness: number) => void,
    onRunComplete?: (runNumber: number, totalRuns: number, intermediateResults?: OptimizationResultsWithStats) => void,
    onAlgorithmComplete?: (algorithm: string, result: AlgorithmResult, partialResults: Partial<OptimizationResults>) => void,
    shouldCancel?: () => boolean,
    skipHeavyCalculations: boolean = false
  ): Promise<{
    results: OptimizationResultsWithStats;
    convergenceData: ConvergenceData;
  }> {
    const numberOfRuns = parameters.numberOfRuns || 1;
    const includeSflaGaBaseline = parameters.includeSflaGaBaseline ?? false;
    
    if (numberOfRuns === 1) {
      // Single run - no statistics needed
      const { results, convergenceData } = await this.runOptimization(
        parameters,
        onAlgorithmStart,
        onGenerationUpdate,
        onAlgorithmComplete,
        shouldCancel
      );
      return {
        results: {
          ...results,
          timestamp: new Date().toISOString(),
          runNumber: 1,
        },
        convergenceData,
      };
    }

    // Multiple runs - collect results and calculate statistics
    const allRuns: OptimizationResults[] = [];
    let lastConvergenceData: ConvergenceData | null = null;

    for (let run = 0; run < numberOfRuns; run++) {
      // Check for cancellation before starting each run
      if (shouldCancel && shouldCancel()) {
        throw new Error('Optimization cancelled');
      }
      
      const { results, convergenceData } = await this.runOptimization(
        parameters,
        (algorithm) => {
          if (onAlgorithmStart) {
            onAlgorithmStart(`${algorithm} (Run ${run + 1}/${numberOfRuns})`);
          }
        },
        (algorithm, generation, fitness) => {
          if (onGenerationUpdate) {
            // Adjust progress to account for multiple runs
            // Each run contributes (100 / numberOfRuns) to overall progress
            // Within each run, we track generation progress as before
            // Extract just the algorithm name (remove run info)
            const algName = algorithm.split(' (Run')[0];
            onGenerationUpdate(algName, generation, fitness);
          }
        },
        (algorithm, result, partialResults) => {
          if (onAlgorithmComplete) {
            // Pass through the callback with algorithm name (without run suffix)
            const algName = algorithm.split(' (Run')[0];
            onAlgorithmComplete(algName, result, partialResults);
          }
        },
        shouldCancel,
        run + 1 // Pass run number for seed generation
      );

      allRuns.push(results);
      lastConvergenceData = convergenceData; // Store convergence data from last run

      // OPTIMIZATION: During complete run, skip all intermediate heavy calculations
      // Only calculate final statistics at the end of all runs for this task count
      if (!skipHeavyCalculations) {
        // For regular optimization (not complete run), calculate intermediate statistics
        // Only calculate on last run or every 5 runs to reduce overhead
        const shouldCalculateIntermediate = run === numberOfRuns - 1 || (run + 1) % 5 === 0;
        
        if (shouldCalculateIntermediate) {
          // Calculate intermediate statistics after each run (PROGRESSIVE UPDATE)
          const intermediateStatistics: OptimizationResultsWithStats['statistics'] = {
            ga: calculateAlgorithmStatistics(allRuns.map(r => r.ga)),
            culture: calculateAlgorithmStatistics(allRuns.map(r => r.culture)),
            frog: calculateAlgorithmStatistics(allRuns.map(r => r.frog)),
            ...(includeSflaGaBaseline
              ? {
                  sflaGa: calculateAlgorithmStatistics(
                    allRuns
                      .map(r => r.sflaGa)
                      .filter((result): result is AlgorithmResult => Boolean(result))
                  ),
                }
              : {}),
            sflaga: calculateAlgorithmStatistics(allRuns.map(r => r.sflaga)),
            pso: calculateAlgorithmStatistics(allRuns.map(r => r.pso)),
            gapso: calculateAlgorithmStatistics(allRuns.map(r => r.gapso)),
          };

          // Calculate intermediate normalization and composite scores
          // Note: Normalization is done per task size (allRuns contains runs for a single task size)
          const intermediateNormalized = normalizeAcrossAlgorithms(allRuns);
          const intermediateCompositeScores = calculateCompositeScoreStatistics(intermediateNormalized, DEFAULT_COMPOSITE_WEIGHTS);

          // Calculate intermediate Pareto analysis
          const intermediateParetoAnalysis = performParetoAnalysis(allRuns);

          // Create intermediate results with current statistics
          const intermediateResults: OptimizationResultsWithStats = {
            ...results,
            statistics: intermediateStatistics,
            compositeScores: intermediateCompositeScores.perAlgorithm,
            paretoAnalysis: intermediateParetoAnalysis,
            timestamp: new Date().toISOString(),
            runNumber: run + 1,
          };

          // Notify that a run is complete with intermediate results for UI update
          if (onRunComplete) {
            onRunComplete(run + 1, numberOfRuns, intermediateResults);
          }
        } else {
          // For non-intermediate calculation runs, just notify progress without statistics
          if (onRunComplete) {
            onRunComplete(run + 1, numberOfRuns, undefined);
          }
        }
      } else {
        // During complete run: skip all intermediate calculations, just notify progress
        // Heavy calculations (statistics, normalization, Pareto) will be done only at the end
        if (onRunComplete) {
          onRunComplete(run + 1, numberOfRuns, undefined);
        }
      }
    }

    // Final statistics - always calculated at the end (required for results)
    const statistics: OptimizationResultsWithStats['statistics'] = {
      ga: calculateAlgorithmStatistics(allRuns.map(r => r.ga)),
      culture: calculateAlgorithmStatistics(allRuns.map(r => r.culture)),
      frog: calculateAlgorithmStatistics(allRuns.map(r => r.frog)),
      ...(includeSflaGaBaseline
        ? {
            sflaGa: calculateAlgorithmStatistics(
              allRuns
                .map(r => r.sflaGa)
                .filter((result): result is AlgorithmResult => Boolean(result))
            ),
          }
        : {}),
      sflaga: calculateAlgorithmStatistics(allRuns.map(r => r.sflaga)),
      pso: calculateAlgorithmStatistics(allRuns.map(r => r.pso)),
      gapso: calculateAlgorithmStatistics(allRuns.map(r => r.gapso)),
    };

    // OPTIMIZATION: Skip heavy calculations during complete run
    // These will be calculated after complete run finishes if needed
    let normalizedResults: ReturnType<typeof normalizeAcrossAlgorithms> | null = null;
    let compositeScores: ReturnType<typeof calculateCompositeScoreStatistics> | null = null;
    let paretoAnalysis: ReturnType<typeof performParetoAnalysis> | null = null;

    if (!skipHeavyCalculations) {
      // Calculate normalization and composite scores (only for regular optimization)
      // IMPORTANT: Normalization is done per task size (allRuns contains runs for a single task size)
      // In complete runs, each task size is normalized independently, not across all task sizes
      normalizedResults = normalizeAcrossAlgorithms(allRuns);
      compositeScores = calculateCompositeScoreStatistics(normalizedResults, DEFAULT_COMPOSITE_WEIGHTS);

      // Perform Pareto analysis (only for regular optimization)
      paretoAnalysis = performParetoAnalysis(allRuns);
    }

    // Use the last run's results as the representative result
    const lastRun = allRuns[allRuns.length - 1];

    return {
      results: {
        ...lastRun,
        statistics,
        compositeScores: compositeScores?.perAlgorithm,
        paretoAnalysis: paretoAnalysis || undefined,
        timestamp: new Date().toISOString(),
        runNumber: numberOfRuns,
      },
      convergenceData: lastConvergenceData!,
    };
  }
}
